import va from '@vercel/analytics';
import { AccessTime, DoNotDisturbAlt, Done, LogoutRounded } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Modal, Tab, Tabs } from '@mui/material';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LoyaltyChallenge, LoyaltyChallengeStatus, LoyaltyChallengeSubmissionData, LoyaltyChallengeSubmissionMin } from '../../pages/api/loyalty/challenges';
import { LoyaltyReward } from '../../pages/api/loyalty/rewards';
import styles from '../../styles/Loyalty.module.css';
import Reward from './Reward';
import { useRouter } from 'next/router';
import { ColorContext } from './Contexts';
import { SubmissionStatusIcon } from './Challenge/ChallengeDetail';

const Challenge = dynamic(() => import('./Challenge'));
const Login = dynamic(() => import('./Login'));

interface ActionProps {
    challenge: LoyaltyChallenge;
    onPress?: () => void;
}

const Action = ({ challenge, onPress }: ActionProps) => {
    const color = useContext(ColorContext);
    const pending = useMemo(() => challenge.submissions.filter(s => s.status === LoyaltyChallengeStatus.PENDING).length, [challenge]);
    const completed = useMemo(() => challenge.submissions.filter(s => s.status === LoyaltyChallengeStatus.COMPLETED).length, [challenge]);
    const denied = useMemo(() => challenge.submissions.filter(s => s.status === LoyaltyChallengeStatus.DENIED).length, [challenge]);
    const canSubmit = pending + completed < challenge.limit;

    function getButtonText() {
        if (canSubmit) {
            if (denied > 0) {
                return 'retry';
            }
            if (completed === 0) {
                return 'start';
            }
            return 'continue';
        }
        if (pending > 0) {
            return 'pending';
        }
        return 'completed';
    }

    return <div>

        <div className={styles.action}>
            <div className={styles.actionPoints}>+{challenge.points}{challenge.limit > 1 && <span style={{ fontSize: 12, fontWeight: 500 }}>x{challenge.limit}</span>}</div>
            <div className={styles.actionTitle}>{challenge.title}</div>
            <div className={styles.rewardRight}>
                <button className={styles.button} style={{ color, backgroundColor: color + '2b' }} onClick={onPress} disabled={!canSubmit}>
                    {getButtonText()}
                </button>
                {challenge.limit > 1 &&
                    completed < challenge.limit &&
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div className={styles.rewardPoints} style={{ marginRight: 4 }}>{completed}/{challenge.limit} <SubmissionStatusIcon status={LoyaltyChallengeStatus.COMPLETED} /></div>
                        {pending > 0 && <div className={styles.rewardPoints} style={{ marginRight: 4 }}>{pending} <SubmissionStatusIcon status={LoyaltyChallengeStatus.PENDING} /></div>}
                        {denied > 0 && <div className={styles.rewardPoints} style={{ marginRight: 4 }}>{denied} <SubmissionStatusIcon status={LoyaltyChallengeStatus.DENIED} /></div>}
                    </div>
                }
            </div>
        </div>
    </div>
}

interface RewardTileProps {
    points: number;
    title: string;
    description: string | null;
    unlocked: boolean;
    canUnlock: boolean;
    limit: number | null;
    users: number;
    onPress?: () => void;
}

const RewardTile = (props: RewardTileProps) => {
    const color = useContext(ColorContext);
    return <div className={styles.action}>
        <div>
            <div className={styles.actionTitle}>{props.title}</div>
            <div className={styles.rewardDescription}>{props.description}</div>
        </div>
        <div className={styles.rewardRight}>
            <button
                className={styles.button}
                style={{ color, backgroundColor: color + '2b' }}
                onClick={props.onPress}
                disabled={!props.canUnlock && !props.unlocked && (!props.limit || props.limit > props.users)}
            >
                {props.unlocked ? 'View' : props.canUnlock ? 'Unlock' : 'Locked'}
            </button>
            {props.limit && <div className={styles.rewardPoints}>{props.limit - props.users}/{props.limit} remaining</div>}
            <div className={styles.rewardPoints}>{props.points} pts</div>
        </div>
    </div>
}

function sortChallenges(challenges: LoyaltyChallenge[]) {
    return challenges.sort((a, b) => {
        const aUncompleted = a.submissions.filter(s => s.status === LoyaltyChallengeStatus.COMPLETED).length < a.limit;
        const bUncompleted = b.submissions.filter(s => s.status === LoyaltyChallengeStatus.COMPLETED).length < b.limit;
        return aUncompleted && !bUncompleted ? -1 : 1;
    });
}

const Loyalty = ({
    artist,
    title,
    subtitle,
    image,
    logo
}: {
    artist: string;
    title: string;
    subtitle: string;
    image?: JSX.Element;
    logo: any;
}) => {
    const router = useRouter();
    const [tab, setTab] = useState("collect");
    const [selectedReward, setSelectedReward] = useState<LoyaltyReward>();
    const [selectedChallenge, setSelectedChallenge] = useState<LoyaltyChallenge>();
    const [selectedSubmission, setSelectedSubmission] = useState<LoyaltyChallengeSubmissionMin>();
    const [challenges, setChallenges] = useState<LoyaltyChallenge[]>();
    const [rewards, setRewards] = useState<LoyaltyReward[]>();
    const [username, setUsername] = useState<string>();
    const [loading, setLoading] = useState(true);
    const points = useMemo(() => {
        const collectedPoints = (challenges || []).reduce((prevC, currentC) =>
            prevC + (currentC.submissions.reduce((prevS, currentS) =>
                prevS + (currentS.status === LoyaltyChallengeStatus.COMPLETED ? currentS.points : 0)
                , 0)), 0);

        const usedPoints = (rewards || []).reduce((prev, current) => prev + (current.unlocked ? current.points : 0), 0);
        return collectedPoints - usedPoints;
    }, [challenges, rewards]);
    const pendingPoints = useMemo(() =>
        (challenges || []).reduce((prevC, currentC) =>
            prevC + (currentC.submissions.reduce((prevS, currentS) =>
                prevS + (currentS.status === LoyaltyChallengeStatus.PENDING ? currentS.points : 0)
                , 0)), 0),
        [challenges]);

    const getData = useCallback(() => {
        if (!router.isReady) return;

        setLoading(true);
        const username = localStorage.getItem(`ens`);
        if (!username) {
            setLoading(false);
            return;
        }
        setUsername(username);

        async function getChallenges() {
            const referrer = router.query.referrer ? `&referrer=${router.query.referrer}` : '';
            const res = await fetch(`/api/loyalty/challenges?artist=${artist}${referrer}`);
            if (!res.ok) {
                if (res.status === 401) {
                    logout();
                    setLoading(false)
                }
                return;
            }
            const { challenges } = await res.json();
            const sorted = sortChallenges(challenges);
            setChallenges(sorted);
            if (referrer) {
                router.replace('', undefined, { shallow: true });
            }
        }

        async function getRewards() {
            const res = await fetch('/api/loyalty/rewards?artist=' + artist);
            if (!res.ok) {
                if (res.status === 401) {
                    logout();
                    setLoading(false)
                }
                return;
            }

            const { rewards } = await res.json();
            setRewards(rewards);
        }

        Promise.all([
            getRewards(),
            getChallenges()
        ])
            .then(() => setLoading(false));
    }, [router.isReady]);

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        // link / QR code auto submit
        if (!router.query.challenge || !challenges) return;
        const challengeId = Number(router.query.challenge);
        const challenge = challenges.find(c => c.id === challengeId);
        if (!challenge || challenge.submissions.find(s => s.status === LoyaltyChallengeStatus.COMPLETED)) return;
        setSelectedChallenge(challenge);
    }, [router.query, challenges]);

    async function onComplete(challenge: LoyaltyChallenge, data?: LoyaltyChallengeSubmissionData) {
        va.track('complete_challenge_' + challenge.type);
        const res = await fetch('/api/loyalty/challenges/' + challenge.id,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: data && JSON.stringify({ data })
            });
        if (!res.ok) {
            throw Error('Complete challenge failed');
        }
        const { submission } = await res.json();
        const updatedChallenge = { ...challenge, submissions: [...challenge.submissions, submission] };
        setChallenges(challenges =>
            sortChallenges((challenges || []).map(c => c.id === challenge.id ? updatedChallenge : c))
        );
        setSelectedChallenge(updatedChallenge);
        setSelectedSubmission(submission);
    }

    async function onUnlockReward(reward: LoyaltyReward, data?: any) {
        const res = await fetch('/api/loyalty/rewards/' + reward.id,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: data && JSON.stringify({ data })
            });
        if (!res.ok) {
            throw Error('Unlocking reward failed');
        }
        const { reward: newReward } = await res.json();
        setRewards(rewards =>
            rewards?.map(r => r.id === reward.id ? newReward : r)
        );
        setSelectedReward(newReward);
    }

    async function logout() {
        fetch(`/api/auth/logout`);
        localStorage.clear();
        setUsername(undefined);
    }

    function onPressLogout() {
        va.track("press_logout");
        logout();
    }

    function onPressChallenge(challenge: LoyaltyChallenge) {
        va.track("press_start_challenge_" + challenge.type);
        setSelectedChallenge(challenge);
    }

    if (loading) {
        return <div className={styles.container}>
            {logo}
            <div className={styles.center}>
                <div style={{ padding: 12 }}>
                    <CircularProgress color='inherit' size={24} />
                </div>
            </div>
        </div>;
    }

    if (!username) {
        return <div className={styles.container}>
            {logo}
            <Login title={title} image={image} subtitle={subtitle} onSuccess={getData} />
        </div>;
    }

    return <>
        <div className={styles.container}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
                <span onClick={onPressLogout}><LogoutRounded /></span>
            </div>
            {logo}
            <div className={styles.center}>
                <div className={styles.pointsBalance}>{points} points</div>
                {pendingPoints > 0 && <div className={styles.fanSince}>{pendingPoints} points pending</div>}
            </div>
            <Tabs variant='fullWidth' value={tab} onChange={(e, value) => setTab(value)}>
                <Tab label="Collect" value="collect" />
                <Tab label="Rewards" value="rewards" />
            </Tabs>
            {tab === "collect" && <>
                {
                    challenges?.map(challenge =>
                        <Action
                            key={challenge.id}
                            challenge={challenge}
                            onPress={() => onPressChallenge(challenge)}
                        />
                    )
                }
            </>}
            {tab === "rewards" &&
                rewards?.map(reward =>
                    <RewardTile
                        key={reward.id}
                        {...reward}
                        canUnlock={points >= reward.points}
                        onPress={() => setSelectedReward(reward)}
                    />)
            }
        </div>
        <Modal open={!!selectedReward} onClose={() => setSelectedReward(undefined)}>
            <div className={styles.modal}>
                <a onClick={() => setSelectedReward(undefined)}><CloseIcon /></a>
                <div className={styles.modalInner}>
                    {selectedReward && <Reward reward={selectedReward} onUnlock={onUnlockReward} />}
                </div>
            </div>
        </Modal>
        <Modal open={!!selectedChallenge}>
            <div className={styles.modal}>
                <a onClick={() => setSelectedChallenge(undefined)}><CloseIcon /></a>
                <div style={{ margin: 12 }}>
                    {
                        selectedChallenge && <Challenge
                            challenge={selectedChallenge}
                            submission={selectedSubmission}
                            onComplete={onComplete}
                            onSubmitMore={() => setSelectedSubmission(undefined)}
                            onClose={() => setSelectedChallenge(undefined)}
                        />
                    }
                </div>
            </div>
        </Modal>
    </>

}

export default Loyalty;